import { useContext, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, InputNumber, Select, Switch, notification } from "antd";
import { SubmitButton } from "@/ui-components/commonComponents/Button/SubmitButton";
import { SEO } from "@/ui-components/commonComponents/SEO/SEO";
import { ClientAbilityContext } from "@/shared/contexts/ability.context";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import { useBeforeunload } from "@/shared/hooks/use-before-unload";
import { useRouteAccessController } from "@/shared/hooks/permissions/use-route-access-controller.hook";
import { Loading, LoadingSize } from "@/shared/components/Loading/Loading";
import { InfoNote } from "@/shared/components/InfoNote/InfoNote";
import { FORM_VALIDATION_ERROR_MESSAGE } from "@/shared/constants/form.constants";

import { TestImportHttpService } from "@/shared/services/tests/test-import-http.service";
import { TestImportFormValues } from "@/shared/services/tests/test-import.types";
import {
  TEST_IMPORT_DEFAULTS,
  AGGREGATION_METHOD_OPTIONS,
  MIN_GROUP_SIZE,
} from "@/shared/services/tests/test-import.constants";

import { ADMIN_ROUTE, NEW_ROUTE, TESTS_ROUTE } from "@/routing/AppRouter/routes.constants";
import { SingleFileDragger } from "@/ui-components/commonComponents/SingleFileDragger/SingleFileDragger";

import { ValidationErrorsModal } from "@/shared/components/ValidationErrorsModal/ValidationErrorsModal";
import style from "./style.module.css";
import { ValidationError } from "@/shared/interfaces/validation-error.interface";

const TOGGLE_FIELDS = [
  {
    name: "skipCompetenciesCreation",
    title: "Assessment Template Required",
    description: "Test will only be available for use within an Assessment Template (not standalone).",
  },
  {
    name: "backButton",
    title: "Back Button",
    description: "Allow the candidate to navigate back to previous questions for this test.",
  },
  {
    name: "autoAdvance",
    title: "Auto-Advance",
    description: "Automatically advance to the next question when a single-question page is answered.",
  },
  {
    name: "groupingEnabled",
    title: "Question Grouping",
    description: "Display questions in grouped blocks with a header (e.g., for Attention to Detail tests).",
  },
];

export default function CreateTest() {
  const navigate = useNavigate();
  const [form] = Form.useForm<TestImportFormValues>();

  const clientAbility = useContext(ClientAbilityContext);
  const canCreateTest = clientAbility.can(Action.Create, Subject.TEST);

  useRouteAccessController({ slug: NEW_ROUTE, hasPermission: canCreateTest });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ValidationError[] | null>(null);

  const colorValue = Form.useWatch("color", form) ?? TEST_IMPORT_DEFAULTS.color;
  const groupingEnabled = Form.useWatch("groupingEnabled", form) ?? false;

  // Needed for a stable reference for the callback in useBeforeunload
  const isSubmittingRef = useRef(false);

  // Triggers a browser "Leave site?" confirmation dialog during form submission
  useBeforeunload(
    useCallback((event: BeforeUnloadEvent) => {
      if (isSubmittingRef.current) {
        event.preventDefault();
      }
    }, []),
  );

  const handleSubmit = async (values: TestImportFormValues) => {
    setIsSubmitting(true);
    isSubmittingRef.current = true;
    setErrors(null);

    try {
      const result = await TestImportHttpService.createTest({
        ...values,
        testName: values.testName.trim(),
        internalName: values.internalName?.trim() || undefined,
      });

      if (!result.success) {
        setErrors(result.errors);
        return;
      }

      notification.success({ message: `Test "${result.testName}" created successfully.` });
      navigate(`/${ADMIN_ROUTE}/${TESTS_ROUTE}/${result.testId}?next-steps=true`);
    } catch (error) {
      notification.error({
        message: "An unexpected error occurred while creating the test. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <div className="main-container">
      <SEO titleParams={["Tests", "New Test"]} />

      {isSubmitting && (
        <div className={style.loadingContainer}>
          <Loading size={LoadingSize.default} />
          <p className={style.loadingTitle}>Creating Test</p>
          <p className={style.loadingSubtext}>Processing your upload, do not close this tab</p>
        </div>
      )}

      <div className={"inner-container"} hidden={isSubmitting}>
        <h1 className={style.pageTitle}>New Test</h1>

        <div className={style.formCard}>
          <Form
            className={style.form}
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={handleSubmit}
            scrollToFirstError={{ block: "center" }}
            onFinishFailed={() => {
              notification.error({ message: FORM_VALIDATION_ERROR_MESSAGE });
            }}
            initialValues={TEST_IMPORT_DEFAULTS}
          >
            <Form.Item label="Test Name" htmlFor="testName">
              <p className={style.fieldDescription} id="testName-desc">
                Display Name for Test.
              </p>
              <Form.Item name="testName" rules={[{ required: true, message: "Test name is required." }]} noStyle={true}>
                <Input id="testName" aria-describedby="testName-desc" />
              </Form.Item>
            </Form.Item>

            <Form.Item label="Internal Name (optional)" htmlFor="internalName">
              <p className={style.fieldDescription} id="internalName-desc">
                Use this to distinguish tests that share a Test Name (e.g., Work Scenarios is a Test Name and
                Situational Judgment - Engineer is an Internal Name).
              </p>
              <Form.Item name="internalName" noStyle={true}>
                <Input id="internalName" aria-describedby="internalName-desc" />
              </Form.Item>
            </Form.Item>

            <Form.Item label="Color" htmlFor="test-color-picker">
              <p className={style.fieldDescription} id="color-desc">
                Hex color code (eg: #57C2A4) used for progress bar during test.
              </p>
              <div className={style.colorInputWrapper}>
                <Form.Item name="color" noStyle={true} rules={[{ required: true, message: "Color is required." }]}>
                  <Input
                    id="test-color-picker"
                    type="color"
                    aria-describedby="color-desc"
                    className={style.colorPickerNative}
                  />
                </Form.Item>
                <span className={style.colorHexLabel}>{colorValue.toUpperCase()}</span>
              </div>
            </Form.Item>

            <Form.Item label="Estimated Completion Time" htmlFor="suggestedTime">
              <p className={style.fieldDescription} id="suggestedTime-desc">
                Suggested completion time in minutes.
              </p>
              <Form.Item
                name="suggestedTime"
                noStyle={true}
                rules={[
                  { required: true, message: "Estimated completion time is required." },
                  { type: "number", min: 1, message: "Must be at least 1 minute." },
                ]}
              >
                <InputNumber
                  id="suggestedTime"
                  min={1}
                  className={style.fullWidth}
                  aria-describedby="suggestedTime-desc"
                  precision={0}
                />
              </Form.Item>
            </Form.Item>

            <Form.Item label="Number of Questions" htmlFor="questionCount">
              <p className={style.fieldDescription} id="questionCount-desc">
                Total number of questions in this test. This should be less than or equal to the total number of
                elements.
              </p>
              <Form.Item
                name="questionCount"
                noStyle={true}
                rules={[
                  { required: true, message: "Number of questions is required." },
                  { type: "number", min: 1, message: "Must be at least 1." },
                ]}
              >
                <InputNumber
                  id="questionCount"
                  min={1}
                  className={style.fullWidth}
                  aria-describedby="questionCount-desc"
                  precision={0}
                />
              </Form.Item>
            </Form.Item>

            <Form.Item label="Scale Aggregation Method" htmlFor="scaleAggregationMethod">
              <p className={style.fieldDescription} id="scaleAggregationMethod-desc">
                How test scale scores will be computed.
              </p>
              <Form.Item name="scaleAggregationMethod" noStyle={true}>
                <Select
                  id="scaleAggregationMethod"
                  options={AGGREGATION_METHOD_OPTIONS}
                  aria-describedby="scaleAggregationMethod-desc"
                />
              </Form.Item>
            </Form.Item>

            {TOGGLE_FIELDS.map(({ name, title, description }) => (
              <div key={name} className={style.toggleRow}>
                <Form.Item name={name} valuePropName="checked" noStyle={true}>
                  <Switch id={name} aria-describedby={`${name}-desc`} />
                </Form.Item>
                <div className={style.toggleLabel}>
                  <label htmlFor={name} className={style.toggleTitle}>
                    {title}
                  </label>
                  <p id={`${name}-desc`} className={style.toggleDesc}>
                    {description}
                  </p>
                </div>
              </div>
            ))}

            {groupingEnabled && (
              <div className={style.groupingFields}>
                <Form.Item label="Group Size" htmlFor="groupSize">
                  <p className={style.fieldDescription} id="groupSize-desc">
                    Number of questions to display together in each group ({MIN_GROUP_SIZE}+).
                  </p>
                  <Form.Item
                    name="groupSize"
                    preserve={
                      false
                    } /* This ensures the field value is cleared from form state when the field unmounts (when the grouping is disabled) */
                    noStyle={true}
                    rules={[
                      { required: true, message: "Group size is required when grouping is enabled." },
                      {
                        type: "number",
                        min: MIN_GROUP_SIZE,
                        message: `Group size must be at least ${MIN_GROUP_SIZE}.`,
                      },
                    ]}
                  >
                    <InputNumber
                      id="groupSize"
                      min={MIN_GROUP_SIZE}
                      className={style.fullWidth}
                      aria-describedby="groupSize-desc"
                      precision={0}
                    />
                  </Form.Item>
                </Form.Item>

                <Form.Item label="Group Header Text" htmlFor="groupHeaderText">
                  <p className={style.fieldDescription} id="groupHeaderText-desc">
                    Header displayed above each question group (e.g., "Check the box if both are the same").
                  </p>
                  <Form.Item
                    name="groupHeaderText"
                    preserve={
                      false
                    } /* This ensures the field value is cleared from form state when the field unmounts (when the grouping is disabled) */
                    noStyle={true}
                    rules={[{ required: true, message: "Group header text is required when grouping is enabled." }]}
                  >
                    <Input id="groupHeaderText" aria-describedby="groupHeaderText-desc" />
                  </Form.Item>
                </Form.Item>
              </div>
            )}

            <Form.Item
              name="zipFile"
              label="Upload Test"
              valuePropName="file"
              rules={[{ required: true, message: "Please upload a ZIP file." }]}
            >
              <SingleFileDragger accept=".zip" hintText="Support for a single ZIP file upload" />
            </Form.Item>

            <InfoNote
              items={[
                <>
                  <strong>Test instructions are entered separately</strong> after test creation on the Test Instructions
                  page.
                </>,
                <>
                  <strong>Norms are uploaded separately</strong> after test creation on the New Norms page.
                </>,
              ]}
            />

            <div className={style.submitRow}>
              <SubmitButton arrowAfter={true}>Submit</SubmitButton>
            </div>
          </Form>
        </div>
      </div>

      <ValidationErrorsModal title="Test Creation Failed" errors={errors} onClose={() => setErrors(null)} />
    </div>
  );
}
